import React, { useState, useEffect } from 'react';
import { 
  KeyIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import { extractLicenses, extractRequests, extractActivationKeys, extractFromSettled } from '../utils/apiHelpers';
import toast from 'react-hot-toast';

const CustomerOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLicenses: 0,
    activeLicenses: 0,
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    activationKeys: 0,
    pendingActivationKeys: 0,
    approvedActivationKeys: 0,
    totalHosts: 0,
    totalSockets: 0,
    totalCores: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set page title
  useEffect(() => {
    document.title = 'Dashboard | Customer Portal';
  }, []);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data with proper error handling
        const [clusterLicensesRes, activationKeysRes] = await Promise.allSettled([
          apiClient.get('/cluster-licenses/my-licenses'),
          apiClient.get('/activation-keys/my-keys')
        ]);



        // Process cluster licenses (apiClient already extracts data)
        const clusterLicenses = clusterLicensesRes.status === 'fulfilled' && Array.isArray(clusterLicensesRes.value) 
          ? clusterLicensesRes.value 
          : [];
        
        const approvedClusterLicenses = clusterLicenses.filter(l => l.status === 'APPROVED');
        const pendingClusterLicenses = clusterLicenses.filter(l => l.status === 'PENDING');

        // Process activation keys (apiClient already extracts data)
        const activationKeys = activationKeysRes.status === 'fulfilled' && Array.isArray(activationKeysRes.value)
          ? activationKeysRes.value
          : [];
        
        const pendingActivationKeys = activationKeys.filter(k => k.status === 'PENDING');
        const approvedActivationKeys = activationKeys.filter(k => k.status === 'APPROVED');

        // Calculate cluster stats from cluster licenses
        let totalHosts = 0;
        let totalSockets = 0;
        let totalCores = 0;
        
        clusterLicenses.forEach(license => {
          if (license.totalHosts) {
            totalHosts += license.totalHosts;
          }
          if (license.totalSockets) {
            totalSockets += license.totalSockets;
          }
          if (license.totalCores) {
            totalCores += license.totalCores;
          }
        });



        setStats({
          totalLicenses: approvedClusterLicenses.length,
          activeLicenses: approvedClusterLicenses.length,
          totalRequests: clusterLicenses.length + activationKeys.length,
          pendingRequests: pendingClusterLicenses.length + pendingActivationKeys.length,
          approvedRequests: approvedClusterLicenses.length + approvedActivationKeys.length,
          activationKeys: activationKeys.length,
          pendingActivationKeys: pendingActivationKeys.length,
          approvedActivationKeys: approvedActivationKeys.length,
          totalHosts,
          totalSockets,
          totalCores
        });

        // Build recent activity from all sources
        const activity = [];
        
        // Add recent cluster licenses
        approvedClusterLicenses.slice(0, 3).forEach(license => {
          activity.push({
            id: `cluster-${license.id}`,
            type: 'cluster',
            message: `Cluster license approved for ${license.hostname || 'Cluster'}`,
            timestamp: new Date(license.createdAt).toLocaleString(),
            icon: CheckCircleIcon,
            color: 'text-green-600'
          });
        });

        // Add pending cluster requests
        pendingClusterLicenses.slice(0, 2).forEach(request => {
          activity.push({
            id: `request-${request.id}`,
            type: 'request',
            message: `Cluster license request pending - ${request.hostname || 'Cluster'}`,
            timestamp: new Date(request.createdAt).toLocaleString(),
            icon: ClockIcon,
            color: 'text-yellow-600'
          });
        });

        // Add recent activation keys
        approvedActivationKeys.slice(0, 2).forEach(key => {
          activity.push({
            id: `activation-${key.id}`,
            type: 'activation',
            message: `Activation key approved for ${key.hostname || 'System'}`,
            timestamp: new Date(key.createdAt).toLocaleString(),
            icon: ShieldCheckIcon,
            color: 'text-purple-600'
          });
        });

        // Sort by timestamp and limit to 8 items
        activity.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setRecentActivity(activity.slice(0, 8));

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const statCards = [
    {
      name: 'Active Licenses',
      value: stats.activeLicenses,
      total: stats.totalLicenses,
      change: `${stats.totalLicenses} total`,
      icon: KeyIcon,
      color: 'bg-green-500',
      link: '/dashboard/my-licenses'
    },
    {
      name: 'Total Hosts',
      value: stats.totalHosts,
      total: stats.totalHosts,
      change: `Across all clusters`,
      icon: ChartBarIcon,
      color: 'bg-blue-500',
      link: null
    },
    {
      name: 'CPU Sockets',
      value: stats.totalSockets,
      total: stats.totalSockets,
      change: `Licensed capacity`,
      icon: ShieldCheckIcon,
      color: 'bg-indigo-500',
      link: null
    },
    {
      name: 'CPU Cores',
      value: stats.totalCores || 0,
      total: stats.totalCores || 0,
      change: stats.totalCores > 0 ? `Total capacity` : 'No data yet',
      icon: DocumentTextIcon,
      color: 'bg-purple-500',
      link: null
    },
    {
      name: 'Pending Requests',
      value: stats.pendingRequests,
      total: stats.totalRequests,
      change: `${stats.approvedRequests} approved`,
      icon: ClockIcon,
      color: 'bg-yellow-500',
      link: '/dashboard/my-requests'
    },
    {
      name: 'Activation Keys',
      value: stats.approvedActivationKeys,
      total: stats.activationKeys,
      change: `${stats.pendingActivationKeys} pending`,
      icon: ShieldCheckIcon,
      color: 'bg-teal-500',
      link: '/dashboard/activation-keys'
    }
  ];

  const quickActions = [
    {
      title: 'Upload Activation Key',
      description: 'Upload new activation key file',
      icon: ShieldCheckIcon,
      link: '/dashboard/activation-keys',
      color: 'text-purple-600'
    },
    {
      title: 'Request Cluster License',
      description: 'Submit a new cluster license request',
      icon: DocumentTextIcon,
      link: '/dashboard/cluster-licenses',
      color: 'text-green-600'
    },
    {
      title: 'View All Licenses',
      description: 'Browse active licenses',
      icon: KeyIcon,
      link: '/dashboard/my-licenses',
      color: 'text-blue-600'
    },
    {
      title: 'Track Requests',
      description: 'Check request status',
      icon: ChartBarIcon,
      link: '/dashboard/my-requests',
      color: 'text-yellow-600'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Overview</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Overview</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Welcome back, {user?.firstName}! Here's your license management summary.</p>
        </div>
        <div className="flex items-center space-x-3">
          {stats.pendingRequests > 0 && (
            <div className="flex items-center px-3 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm font-medium">
              <ClockIcon className="h-4 w-4 mr-2" />
              {stats.pendingRequests} Pending
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-6">
        {statCards.map((stat) => (
          <div 
            key={stat.name} 
            onClick={stat.link ? () => navigate(stat.link) : undefined}
            className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-transparent hover:-translate-y-1 ${
              stat.link ? 'cursor-pointer group' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 group-hover:text-gray-700 transition-colors">{stat.name}</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2 transition-transform duration-300 group-hover:scale-105">{stat.value}</p>
                <p className="text-sm mt-2 text-gray-500 group-hover:text-gray-600 transition-colors">
                  {stat.change}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${stat.color} group-hover:scale-110 transition-all duration-300 shadow-md group-hover:shadow-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2.5 text-[#197560]" />
                Recent Activity
              </h3>
            </div>
            <div className="p-6">
              {recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No recent activity</p>
                  <p className="text-sm text-gray-400 mt-1">Your activity will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-3.5 rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all duration-200 border border-transparent hover:border-gray-100 cursor-pointer group">
                      <div className={`p-2.5 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 group-hover:shadow-md transition-all duration-200`}>
                        <activity.icon className={`h-4 w-4 ${activity.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-2">
              {quickActions.map((action) => (
                <button 
                  key={action.title}
                  onClick={() => navigate(action.link)}
                  className="w-full flex items-center justify-between p-3.5 text-left rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all duration-200 group border border-transparent hover:border-gray-100 hover:shadow-sm"
                >
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 group-hover:shadow-md transition-all duration-200">
                      <action.icon className={`h-5 w-5 ${action.color} group-hover:scale-110 transition-transform duration-200`} />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-[#197560] transition-colors">{action.title}</p>
                      <p className="text-xs text-gray-500 group-hover:text-gray-600 transition-colors">{action.description}</p>
                    </div>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-gray-300 group-hover:text-[#197560] group-hover:translate-x-1 transition-all duration-200" />
                </button>
              ))}
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-br from-[#197560] via-[#1a826c] to-[#145248] rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-shadow duration-300 border border-[#1a826c]">
            <h3 className="text-lg font-semibold mb-5">Cluster Summary</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-4 border-b border-white/20">
                <span className="text-sm opacity-95 font-medium">Total Hosts</span>
                <span className="text-2xl font-bold bg-white/10 px-3 py-1 rounded-lg">{stats.totalHosts}</span>
              </div>
              <div className="flex items-center justify-between pb-4 border-b border-white/20">
                <span className="text-sm opacity-95 font-medium">CPU Sockets</span>
                <span className="text-2xl font-bold bg-white/10 px-3 py-1 rounded-lg">{stats.totalSockets}</span>
              </div>
              <div className="flex items-center justify-between pb-4 border-b border-white/20">
                <span className="text-sm opacity-95 font-medium">CPU Cores</span>
                <span className="text-2xl font-bold bg-white/10 px-3 py-1 rounded-lg">{stats.totalCores || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm opacity-95 font-medium">Active Licenses</span>
                <span className="text-2xl font-bold bg-white/10 px-3 py-1 rounded-lg">{stats.activeLicenses}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOverview;
